/**
 * @file contacts.ts
 * @description Реестр видов контактов в настройках сайта (phase19.16 Р1;
 * SB18 — контакты и соцсети в «Настройках сайта»; SB21 — пустое не
 * показывается; SB58 — мессенджеры обычными ссылками, Instagram не
 * предлагаем; SB85 — значок только при настоящей ссылке, без `#`).
 *
 * Хранение — `SiteDraft.contacts` (JSON): `[{ kind, value, showInFooter,
 * showInMenu }]`. Email без значения = `Shop.contactEmail` (db-design-r1
 * §1): адрес магазина не дублируется.
 *
 * OCP: новый вид — новая запись `SITE_CONTACT_KINDS` (разбор значения и
 * сборка ссылки), без веток в сервере, кабинете и витрине. Ссылки строятся
 * только из разобранного значения, поэтому `javascript:` и подобное в
 * `href` попасть не могут.
 */

import { z } from "zod";

import { SITE_LIMITS } from "./limits.js";

export const SITE_CONTACT_KIND_KEYS = [
  "phone",
  "email",
  "telegram",
  "vk",
  "max",
  "whatsapp",
  "address",
  "site",
] as const;

export type SiteContactKind = (typeof SITE_CONTACT_KIND_KEYS)[number];

/** Итог разбора значения: нормализованное значение или текст ошибки. */
export type SiteContactParse = { ok: true; value: string } | { ok: false; message: string };

/** Описание вида контакта. */
export interface SiteContactKindInfo {
  readonly kind: SiteContactKind;
  readonly label: string;
  readonly placeholder: string;
  /** Соцсеть или мессенджер — в подвале значком (SB85), а не строкой. */
  readonly social: boolean;
  /** Разбор того, что ввёл владелец, в каноническую форму. */
  readonly parse: (raw: string) => SiteContactParse;
  /** Ссылка из канонического значения; `null` — показывается текстом. */
  readonly href: (value: string) => string | null;
}

const fail = (message: string): SiteContactParse => ({ ok: false, message });
const okValue = (value: string): SiteContactParse => ({ ok: true, value });

/** Телефон → `+7XXXXXXXXXX`; российский `8XXXXXXXXXX` приводится к `+7`. */
function parsePhone(raw: string): SiteContactParse {
  const trimmed = raw.trim();
  if (!/^[+\d][\d\s()\-.]*$/.test(trimmed)) return fail("Телефон — цифры, можно с + и пробелами");
  let digits = trimmed.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("8") && !trimmed.startsWith("+")) {
    digits = `7${digits.slice(1)}`;
  }
  if (digits.length === 10 && !trimmed.startsWith("+")) digits = `7${digits}`;
  if (digits.length < 10 || digits.length > 15) return fail("Телефон — от 10 до 15 цифр");
  return okValue(`+${digits}`);
}

/**
 * Ссылка на профиль: полный адрес на одном из `hosts`, адрес без схемы или
 * короткое имя (`@name`). Возвращает путь профиля без ведущего `/`.
 */
function parseProfile(
  raw: string,
  hosts: readonly string[],
  namePattern: RegExp,
  label: string,
): SiteContactParse {
  let value = raw.trim();
  if (value.startsWith("@")) value = value.slice(1);
  const hostPattern = new RegExp(
    `^(?:https?://)?(?:www\\.)?(?:${hosts.map((h) => h.replace(/\./g, "\\.")).join("|")})/`,
    "i",
  );
  if (hostPattern.test(value)) {
    value = value.replace(hostPattern, "").replace(/[/?#].*$/, "");
  } else if (/[/:]/.test(value)) {
    return fail(`${label}: ссылка должна вести на ${hosts[0]}`);
  }
  if (!namePattern.test(value)) return fail(`${label}: не похоже на имя профиля`);
  return okValue(value);
}

const TELEGRAM_NAME = /^[A-Za-z][A-Za-z0-9_]{4,31}$/;
const VK_NAME = /^[A-Za-z0-9_.]{2,64}$/;
const MAX_NAME = /^[A-Za-z0-9_\-/]{2,128}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseSite(raw: string): SiteContactParse {
  const trimmed = raw.trim();
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "https:" && url.protocol !== "http:") return fail("Адрес сайта — http(s)");
    if (!url.hostname.includes(".")) return fail("Адрес сайта — например, example.ru");
    return okValue(url.toString());
  } catch {
    return fail("Адрес сайта — например, example.ru");
  }
}

/** Реестр видов. Instagram намеренно отсутствует (SB58, OQ-107). */
export const SITE_CONTACT_KINDS: Readonly<Record<SiteContactKind, SiteContactKindInfo>> = {
  phone: {
    kind: "phone",
    label: "Телефон",
    placeholder: "+7 900 000-00-00",
    social: false,
    parse: parsePhone,
    href: (value) => `tel:${value}`,
  },
  email: {
    kind: "email",
    label: "Email",
    placeholder: "info@example.ru",
    social: false,
    parse: (raw) => {
      const value = raw.trim().toLowerCase();
      return EMAIL.test(value) && value.length <= 254 ? okValue(value) : fail("Неверный email");
    },
    href: (value) => `mailto:${value}`,
  },
  telegram: {
    kind: "telegram",
    label: "Telegram",
    placeholder: "@username или https://t.me/username",
    social: true,
    parse: (raw) => parseProfile(raw, ["t.me", "telegram.me"], TELEGRAM_NAME, "Telegram"),
    href: (value) => `https://t.me/${value}`,
  },
  vk: {
    kind: "vk",
    label: "ВКонтакте",
    placeholder: "https://vk.com/name",
    social: true,
    parse: (raw) => parseProfile(raw, ["vk.com", "vk.ru", "m.vk.com"], VK_NAME, "ВКонтакте"),
    href: (value) => `https://vk.com/${value}`,
  },
  max: {
    kind: "max",
    label: "MAX",
    placeholder: "https://max.ru/…",
    social: true,
    // Формат публичных адресов MAX ещё не устоялся: принимаем путь на max.ru
    // целиком (буквы, цифры, «_», «-», «/»), ссылку собираем обратно.
    parse: (raw) => {
      const trimmed = raw.trim();
      const match = /^(?:https?:\/\/)?(?:www\.)?max\.ru\/(.+)$/i.exec(trimmed);
      if (!match) return fail("MAX: ссылка должна вести на max.ru");
      const path = (match[1] ?? "").replace(/[?#].*$/, "").replace(/\/+$/, "");
      return MAX_NAME.test(path) ? okValue(path) : fail("MAX: не похоже на ссылку профиля");
    },
    href: (value) => `https://max.ru/${value}`,
  },
  whatsapp: {
    kind: "whatsapp",
    label: "WhatsApp",
    placeholder: "+7 900 000-00-00",
    social: true,
    parse: (raw) => {
      const match = /^(?:https?:\/\/)?(?:wa\.me|api\.whatsapp\.com\/send\?phone=)\/?(\+?\d+)/i.exec(
        raw.trim(),
      );
      return parsePhone(match?.[1] ?? raw);
    },
    href: (value) => `https://wa.me/${value.replace(/^\+/, "")}`,
  },
  address: {
    kind: "address",
    label: "Адрес",
    placeholder: "г. Город, ул. Улица, 1",
    social: false,
    parse: (raw) => {
      const value = raw.trim().replace(/\s+/g, " ");
      if (value.length < 3) return fail("Адрес слишком короткий");
      if (value.length > 300) return fail("Адрес — не длиннее 300 знаков");
      return okValue(value);
    },
    // Ссылку на карты не строим: выбор сервиса карт — решение магазина, не платформы.
    href: () => null,
  },
  site: {
    kind: "site",
    label: "Сайт",
    placeholder: "https://example.ru",
    social: true,
    parse: parseSite,
    href: (value) => value,
  },
};

/** Сужение строки до вида контакта. */
export function isSiteContactKind(kind: string): kind is SiteContactKind {
  return (SITE_CONTACT_KIND_KEYS as readonly string[]).includes(kind);
}

/** Разбор значения контакта нужного вида. */
export function parseSiteContactValue(kind: SiteContactKind, raw: string): SiteContactParse {
  return SITE_CONTACT_KINDS[kind].parse(raw);
}

/**
 * Ссылка для уже разобранного значения. Значение перепроверяется: в JSON
 * могло лечь что угодно, а `href` уходит в разметку витрины.
 */
export function buildSiteContactHref(kind: SiteContactKind, value: string): string | null {
  const info = SITE_CONTACT_KINDS[kind];
  const parsed = info.parse(value);
  return parsed.ok ? info.href(parsed.value) : null;
}

/** Контакт в настройках сайта. */
export const siteContactItemSchema = z
  .strictObject({
    kind: z.enum(SITE_CONTACT_KIND_KEYS),
    /** null допустим только у email — тогда берётся `Shop.contactEmail`. */
    value: z.string().max(500).nullable(),
    showInFooter: z.boolean(),
    showInMenu: z.boolean(),
  })
  .superRefine((item, ctx) => {
    if (item.value === null) {
      if (item.kind !== "email") {
        ctx.addIssue({ code: "custom", path: ["value"], message: "Укажите значение контакта" });
      }
      return;
    }
    const parsed = parseSiteContactValue(item.kind, item.value);
    if (!parsed.ok) ctx.addIssue({ code: "custom", path: ["value"], message: parsed.message });
  });

export const siteContactsSchema = z
  .array(siteContactItemSchema)
  .max(SITE_LIMITS.contacts, `Контактов — не больше ${SITE_LIMITS.contacts}`);

export type SiteContactItem = z.infer<typeof siteContactItemSchema>;
